import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef} from 'react';
import AceEditor from 'react-ace';

import UserConstants from 'constants/UserConstants';
import {SvixResponseStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getLocaleName} from 'i18n/LanguageTags';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import {Header1} from 'ui-library/Headers';
import useOnMount from 'ui-library/hooks/useOnMount';
import Spinner from 'ui-library/Spinner';

import {GetWebhookResponse, GetWebhookResponseVariables} from './__generated/GetWebhookResponse';
import {WebhookResponse, ResponseState} from './BenchlingAppWebhookPlayground';
import {disableEditorHighlightAndCursor} from './WebhookPlaygroundPreview';

export const GET_WEBHOOK_RESPONSE_QUERY = gql`
  query GetWebhookResponse($appDefId: StrictString!, $messageId: StrictString!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    webhookResponse(appDefId: $appDefId, messageId: $messageId) {
      ... on InaccessibleInfoNode {
        invalidId
      }
      ... on WebhookResponseNode {
        status
        url
        response
        responseCode
        id
        timestamp
        truncated
        truncateLimit
      }
    }
  }
`;

const BENCHLING_APP_PLAYGROUND_POLLING_FREQUENCY_MS = 1000;

interface WebhookPlaygroundResponseProps {
  responseData: WebhookResponse;
}

export default function WebhookPlaygroundResponse(props: WebhookPlaygroundResponseProps): JSX.Element {
  const {__typename, id, status, truncated, truncateLimit, ...userResponseData} = props.responseData;
  const isSuccessfulResponse = status === SvixResponseStatus.SUCCESS;

  const locale = getLocaleName(UserConstants.LANGUAGE_PREFERENCE_KEY);
  const timestamp = new Date(userResponseData.timestamp).toLocaleString(locale);

  const editorRef = useRef<AceEditor | null>(null);

  useOnMount(() => {
    disableEditorHighlightAndCursor(editorRef.current!);
  });

  return (
    <div>
      <div className={truncated ? css(styles.truncateBanner) : css(styles.banner)}>
        <InlineBanner
          bannerType={isSuccessfulResponse ? InlineBannerType.SUCCESS : InlineBannerType.ERROR}
          text={isSuccessfulResponse ? 'Success' : `Error: ${userResponseData.responseCode}`}
          supportingInfo={`Sent at ${timestamp}`}
        />
      </div>
      {truncated && (
        <div className={css(styles.truncateDescription)}>{`Displaying only ${truncateLimit} characters`}</div>
      )}
      <AceEditor
        ref={editorRef}
        mode="json"
        theme="textmate"
        value={JSON.stringify(userResponseData, null, 4)}
        readOnly={true}
        width="100%"
        height={`${truncated ? 345 : 370}px`}
        wrapEnabled={true}
        highlightActiveLine={false}
        editorProps={{$blockScrolling: true}}
      />
    </div>
  );
}

export function WebhookPlaygroundInitResponse(): JSX.Element {
  return (
    <div className={css(styles.table)}>
      <div className={css(styles.responseCol)}>
        <Header1>Send test message</Header1>
        <p className={css(styles.responseDescription)}>
          Send a test webhook message. Your app's response will appear here.
        </p>
      </div>
    </div>
  );
}

interface WebhookPlaygroundLoadingProps {
  appDefinitionId: string;
  svixMessageId: string | null;
  setResponseState: (responseState: ResponseState) => void;
  setResponseData: (responseData: WebhookResponse | null) => void;
  setSvixMessageId: (svixMessageId: string | null) => void;
}

export function WebhookPlaygroundLoading(props: WebhookPlaygroundLoadingProps): JSX.Element {
  const {appDefinitionId, svixMessageId, setResponseState, setResponseData, setSvixMessageId} = props;

  const webhookResponseQuery = useSkippableBenchlingQuery<GetWebhookResponse, GetWebhookResponseVariables>(
    GET_WEBHOOK_RESPONSE_QUERY,
    {
      pollInterval: BENCHLING_APP_PLAYGROUND_POLLING_FREQUENCY_MS,
      skip: svixMessageId == null,
      variables: {
        appDefId: appDefinitionId,
        messageId: svixMessageId!,
      },
    }
  );

  useEffect(() => {
    if (webhookResponseQuery.type === QueryResultType.SUCCESS) {
      if (!isInaccessible(webhookResponseQuery.data.webhookResponse)) {
        const webhookResponse = webhookResponseQuery.data.webhookResponse;
        if (webhookResponse.status !== SvixResponseStatus.PENDING) {
          setResponseState(ResponseState.RESPONSE);
          setResponseData(webhookResponse);
          setSvixMessageId(null);
        }
      }
    }
  }, [webhookResponseQuery, setResponseState, setResponseData, setSvixMessageId]);

  return (
    <div className={css(styles.table)}>
      <div className={css(styles.responseCol)}>
        <Header1>Wait for response</Header1>
        <p className={css(styles.responseDescription)}>Verify app responses when message is delivered.</p>
        <Spinner light={true} />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  banner: {
    marginBottom: '12px',
  },
  responseCol: {
    textAlign: 'center',
    verticalAlign: 'middle',
    display: 'table-cell',
  },
  responseDescription: {
    padding: '0px 120px 10px 120px',
  },
  table: {
    display: 'table',
    height: '100%',
  },
  truncateBanner: {
    marginBottom: '8px',
  },
  truncateDescription: {
    color: GRAY_SEMIDARK,
    fontSize: '13px',
    marginBottom: '8px',
  },
});
